import { addComment, changeComment } from '@/utils/comment';
import { deepClone } from '@/utils/deepClone';
import { generateUUID } from '@/utils/other';
import ListClass, { IListItem } from './ListClass';
import RequestApi from './RequestApiClass';

type ILabelInfo = ILabel.IResponse.ILabelInfo;
type ICreateOrUpdateComment = IComment.IRequest.ICreateOrUpdateComment;
type IUserDetailInfo = IUser.IResponse.IUserDetailInfo;

export interface IBaseItem extends IListItem {
  id: string;
  users?: IUserDetailInfo[];
  comments: IComment.IResponse.ICommentInfo[];
  labels: ILabelInfo[];
}

const defaultParams = {
  pageindex: 1,
  pagesize: 100,
};

interface IBaseClassConstructorOption<T extends IBaseItem> {
  list: ListClass<T>;
  requestApi: RequestApi;
}

export default class BaseListClass<T extends IBaseItem> {
  public $requestParams: any = { ...defaultParams };
  public detailParams: any = {};
  public isMore: boolean = false;
  protected $list: ListClass<T>;
  protected $requestApi: RequestApi;
  private $editItem: T | undefined = undefined;
  get list() {
    return this.$list;
  }
  get requestApi() {
    return this.$requestApi;
  }
  get editItem() {
    return this.$editItem;
  }
  constructor(option: IBaseClassConstructorOption<T>) {
    this.$list = option.list;
    this.$requestApi = option.requestApi;
  }
  public requestData<Q>(params?: Q): Promise<string> {
    this.initRequestParams();
    const newParams = Object.assign({}, params);
    this.updateRequestParams(newParams);
    return new Promise((reslove, reject) => {
      this.$requestApi
        .getData(this.$requestApi.baseUrl, this.$requestParams)
        .then((res) => {
          this.initListData(res.data);
          this.updateIsMore(res.data.length);
          if (res.data.length > 0) {
            reslove(res.message);
          } else {
            reject('暂无数据');
          }
        })
        .catch((error) => {
          reject(error.message);
        });
    });
  }
  public requestMoreData(): Promise<string> {
    this.$requestParams.pageindex += 1;
    return new Promise((reslove, reject) => {
      this.$requestApi
        .getData(this.$requestApi.baseUrl, this.$requestParams)
        .then((res) => {
          this.$list.pushItemList(this.convertData(res.data));
          this.updateIsMore(res.data.length);
          reslove(res.message);
        })
        .catch((error) => {
          reject(error.message);
        });
    });
  }
  public requestDeleteById(id: string): Promise<string> {
    return new Promise((reslove, reject) => {
      this.$requestApi
        .deleteData(this.$requestApi.createUrlById(id))
        .then((res) => {
          this.$list.deleteItemById(id);
          reslove(res.message);
        })
        .catch((error) => {
          reject(error.message);
        });
    });
  }
  public requestDetail<Q>(params: Q): Promise<string> {
    return new Promise((reslove, reject) => {
      this.$requestApi
        .getDataByCancel(this.$requestApi.baseUrl, {...params, ...this.detailParams})
        .then((res) => {
          if (res.data.length > 0) {
            this.resetItem(res.data[0]);
            reslove(res.message);
          } else {
            reject('暂无具体数据');
          }
        })
        .catch((err) => {
          reject(err.message);
        });
    });
  }
  public requestUpdateStatus<Q>(item: T, status: Q): Promise<string> {
    return new Promise((reslove, reject) => {
      this.$requestApi
        .putData(this.$requestApi.createStatusUrlById(item.id), null, {
          status,
        })
        .then((res) => {
          this.$list.updateItem({ ...item, status });
          reslove(res.message);
        })
        .catch((error) => {
          reject(error.message);
        });
    });
  }
  public requestChangeItem<Q>(item: T, updateParams: Q): Promise<string> {
    return new Promise((reslove, reject) => {
      this.$requestApi
        .postData(this.$requestApi.createUrlById(item.id), updateParams)
        .then((res) => {
          this.$list.updateItem(Object.assign({}, item, updateParams));
          reslove(res.message);
        })
        .catch((err) => {
          reject(err.message);
        });
    });
  }
  public requestCreateItem<Q>(updateParams: Q): Promise<string> {
    const id = generateUUID();
    return new Promise((reslove, reject) => {
      this.$requestApi
        .postData(this.$requestApi.createUrlById(id), updateParams)
        .then((res1) => {
          this.requestDataForUnshift(id)
            .then((res2) => {
              reslove(res2);
            })
            .catch((err) => {
              reject(err);
            });
        })
        .catch((err) => {
          reject(err.message);
        });
    });
  }
  public requestDataForUnshift(id: string): Promise<string> {
    return new Promise((reslove, reject) => {
      this.$requestApi
        .getData(this.$requestApi.baseUrl, { id })
        .then((res) => {
          if (res.data.length > 0) {
            this.unshiftToList(this.convertData(res.data)[0]);
            reslove(res.message);
          } else {
            reject('Error');
          }
        })
        .catch((error) => {
          reject(error.message);
        });
    });
  }
  public resetItem(updateInfo: T) {
    const newItem = this.convertData([updateInfo])[0];
    this.$list.updateItem(newItem);
    if (this.$list.current && this.$list.current.id === newItem.id) {
      this.$list.changeCurrent({ ...newItem });
    }
  }
  public initRequestParams() {
    this.$requestParams = { ...defaultParams };
  }
  public updateRequestParams<Q>(params: Q) {
    Object.assign(this.$requestParams, params);
  }
  public updateIsMore(length: number) {
    if (length < this.$requestParams.pagesize!) {
      this.isMore = false;
    } else {
      this.isMore = true;
    }
  }
  public initListData(items: T[]) {
    this.$list.initData(this.convertData(items));
    if (!this.$list.current) {
      return;
    }
    if (this.$list.checkItemIsInData(this.$list.current.id)) {
      this.requestDetail({
        id: this.$list.current.id,
      });
    } else {
      this.$list.changeCurrent(undefined);
    }
  }
  public unshiftToList(item: T) {
    if (this.checkItem(item)) {
      this.$list.unshiftItem(item);
    }
  }
  public changeEditItem(item: T) {
    this.$editItem = item;
  }
  public updateLabel(listItem: T, labels: ILabelInfo[]) {
    listItem.labels = deepClone(labels);
    this.$list.updateItem({...listItem});
  }
  public updateComment(
    type: 'ADD' | 'CHANGE',
    listItem: T,
    comment: ICreateOrUpdateComment,
  ) {
    if (type === 'ADD') {
      listItem.comments = addComment(listItem.comments, comment);
      this.$list.updateItem({...listItem});
      return;
    }
    listItem.comments = changeComment(listItem.comments, comment);
    this.$list.updateItem({...listItem});
  }
  public updateReminder(listItem: T, reminders: IUserDetailInfo[]) {
    listItem.users = deepClone(reminders);
    this.$list.updateItem({...listItem});
  }
  public convertData(items: T[]): T[] {
    return items.map((item) => item);
  }
  public checkItem(item: T) {
    return true;
  }
}
